import React, { useEffect } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { ReportVisibilityResult, useReportVisibility } from '@fd/customHooks/useReportVisibility';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';

import SettingsList, { SettingsMenuItem } from '@fd/ui/List/SettingsList';

import { flagService } from '../../services';
import useLookerDashboards from './customHooks/useLookerDashboards';
import { getReportsMenuItems, VisibilityContext } from './utils/getReportsMenuItems';

type ReportsMenuItemsListProps = {
  appId: string;
  reportType?: 'main' | 'rms';
  onError?: (error: unknown) => void;
};

const CircularProgressContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
}));

// TODO: Remove when reportCategoryVisibility flag is removed (keep only category mode)
function buildVisibilityContext(reportVisibility: ReportVisibilityResult): VisibilityContext {
  if (reportVisibility.mode === 'category') {
    const category =
      reportVisibility.categoryResult.status === 'categorized'
        ? reportVisibility.categoryResult.category
        : 'ONLINE_ONLY_NON_RMS';
    return { mode: 'category', category, subscriptions: reportVisibility.subscriptions };
  }
  return {
    mode: 'legacy',
    menuSource: reportVisibility.menuSource,
    subscriptions: reportVisibility.subscriptions,
  };
}

function ReportsMenuItemsList(props: ReportsMenuItemsListProps): JSX.Element | null {
  const { appId, reportType = 'main', onError } = props;
  const reportVisibility = useReportVisibility(appId);
  const showLabels = useAppSelector((state) => flagService.isFlagOn(state, 'reportLabels'));

  const {
    data: lookerDashboardsData = [],
    isLoading: isLoadingLookerDashboards,
    error: lookerDashboardsError,
  } = useLookerDashboards(appId);

  useEffect(() => {
    if (lookerDashboardsError) {
      onError?.(lookerDashboardsError);
    }
  }, [lookerDashboardsError, onError]);

  if (reportVisibility.isLoading || isLoadingLookerDashboards) {
    return (
      <CircularProgressContainer>
        <CircularProgress size={40} data-fd="reports-loading-spinner" />
      </CircularProgressContainer>
    );
  }

  if (
    reportVisibility.mode === 'category' &&
    reportVisibility.categoryResult.status === 'no_storefronts'
  ) {
    return null;
  }

  const visibilityContext = buildVisibilityContext(reportVisibility);
  const allMenuItems = getReportsMenuItems(
    appId,
    lookerDashboardsData,
    visibilityContext,
    showLabels
  );

  const menuItems = allMenuItems.filter((item) => {
    const isRmsReport = item.path.includes('/rms/');
    if (reportType === 'rms') {
      return reportVisibility.showRMSSection && isRmsReport;
    }
    return !isRmsReport;
  });

  const settingsMenuItems: SettingsMenuItem[] = menuItems.map((item) => ({
    title: item.title,
    path: item.path,
    icon: item.icon,
    subtitle: item.subtitle,
    hidden: item.hidden,
    labels: item.labels,
    dataFd: `All_reports_${item.title}`,
  }));

  return (
    <SettingsList
      menuItems={settingsMenuItems}
      shouldShowAsLast={(_item, index) => index === settingsMenuItems.length - 1}
    />
  );
}

export default ReportsMenuItemsList;
